import { ElementProps } from '@/components/sections/builder/types';
import { useBuilderStore } from '@/stores/builder';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ElementDataType } from '@/components/sections/builder/elements/body/multicolumn/type';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import InputTab from '@/components/sections/builder/settings/input-tab';
import { Input } from '@/components/ui/input';
import { shallow } from 'zustand/shallow';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import InputLink from '@/components/sections/builder/settings/input-link';
import { Checkbox } from '@/components/ui/checkbox';
import TextEditorMinimal from '@/components/commons/minimal-tiptap/minimal-editor';

const IMAGE_WIDTHS = [
  { value: 'one_third_width', label: 'One-third width of column' },
  { value: 'half_width', label: 'Half width of column' },
  { value: 'full_width', label: 'Full width of column' },
];

const IMAGE_RATIOS = [
  { value: 'adapt', label: 'Adapt to image' },
  { value: 'potrait', label: 'Potrait' },
  { value: 'square', label: 'Square' },
  { value: 'circle', label: 'Circle' },
];

export default function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setElementData: state.setElementData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const content = element.data as ElementDataType;

  return (
    <div>
      <div className='my-4'>Heading</div>
      <TextEditorMinimal
        value={content.heading as string}
        onChange={(val) => {
          builder.setElementData(element.id, {
            heading: val,
          });
        }}
        output='html'
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
      />
      <div className='my-4'>Heading Size</div>
      <InputTab
        defaultValue={content.headingSize}
        options={[
          { label: 'Small', value: '20' },
          { label: 'Medium', value: '30' },
          { label: 'Large', value: '40' },
        ]}
        onChange={(v) => builder.setElementData(element.id, { headingSize: v })}
      />
      <div className='my-4'>Content Alignment</div>
      <InputTab
        defaultValue={content.contentAlign}
        options={[
          { label: 'Left', value: 'left' },
          { label: 'Center', value: 'center' },
          { label: 'Right', value: 'right' },
        ]}
        onChange={(v) => builder.setElementData(element.id, { contentAlign: v })}
      />
      <div className='mb-2 text-sm md:mt-4'>Image Width</div>
      <div className='mb-4'>
        <Select
          name='imageWidth'
          value={content.imageWidth}
          onValueChange={(val) =>
            builder.setElementData(element.id, {
              imageWidth: val,
            })
          }
        >
          <SelectTrigger className='py-5'>
            <SelectValue placeholder='Select' />
          </SelectTrigger>
          <SelectContent>
            {IMAGE_WIDTHS.map((obj) => (
              <SelectItem key={obj.value} value={obj.value}>
                {obj.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className='mb-2 text-sm md:mt-4'>Image Ratio</div>
      <div className='mb-4'>
        <Select
          name='imageRatio'
          value={content.imageRatio}
          onValueChange={(val) =>
            builder.setElementData(element.id, {
              imageRatio: val,
            })
          }
        >
          <SelectTrigger className='py-5'>
            <SelectValue placeholder='Select' />
          </SelectTrigger>
          <SelectContent>
            {IMAGE_RATIOS.map((obj) => (
              <SelectItem key={obj.value} value={obj.value}>
                {obj.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <InputSlider
        label='Number of columns on desktop'
        value={content.numberOfColumns}
        min={1}
        max={6}
        onChange={(val) =>
          builder.setElementData(element.id, {
            numberOfColumns: val,
          })
        }
      />
      <div className='my-4'>Column Alignment</div>
      <InputTab
        grid={2}
        defaultValue={content.columnAlignment}
        options={[
          { label: 'Left', value: 'left' },
          { label: 'Center', value: 'center' },
        ]}
        onChange={(v) => builder.setElementData(element.id, { columnAlignment: v })}
      />
      <div className='mb-1 mt-4 text-sm'>Secondary Background</div>
      <InputTab
        grid={2}
        defaultValue={content.secondBackground ?? 'false'}
        options={[
          { label: 'None', value: 'false' },
          { label: 'Show as column background', value: 'true' },
        ]}
        onChange={(v) => builder.setElementData(element.id, { secondBackground: v })}
      />
      <div className='mb-1 text-sm md:mt-4'>Button label</div>
      <Input
        onChange={(e) =>
          builder.setElementData(element.id, {
            buttonLabel: e.target.value,
          })
        }
        value={content.buttonLabel}
        name='buttonLabel'
        type='text'
        size={100}
      />
      <div className='mb-1 text-sm md:mt-4'>Button link</div>
      <InputLink
        name='url_path'
        placeholder='Search or enter a link'
        onChange={(value) => {
          builder.setElementData(element.id, {
            buttonLink: value,
          });
        }}
        value={content.buttonLink as string}
      />
      <div className='mb-1 text-sm md:mt-4'>Color Scheme</div>
      <div className='my-4'>
        <SchemeSetting
          value={content.colorScheme}
          onChange={(theme) => {
            builder.setElementData(element.id, {
              colorScheme: theme,
            });
          }}
        />
      </div>
      <div className='mb-4 mt-4'>Number of columns on mobile</div>
      <InputTab
        defaultValue={content.totalColMobile ?? 'one_column'}
        grid={2}
        options={[
          { label: '1 column', value: 'one_column' },
          { label: '2 columns', value: 'two_columns' },
        ]}
        onChange={(v) =>
          builder.setElementData(element.id, {
            totalColMobile: v,
          })
        }
      />
      <div className='my-4 flex gap-x-1'>
        <Checkbox
          id='enableSwipeMobileMultiColumn'
          className='mx-2'
          checked={content.enableSwipeMobile}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              enableSwipeMobile: !!checked,
            });
          }}
        />
        <label htmlFor='enableSwipeMobile'>Enable swipe on mobile</label>
      </div>
      <div className='mb-1 mt-4 text-base font-bold md:mt-4'>Section Padding</div>
      <InputSlider
        label='Top padding'
        value={content.topPadding}
        onChange={(val) =>
          builder.setElementData(element.id, {
            topPadding: val,
          })
        }
      />
      <InputSlider
        label='Bottom padding'
        value={content.bottomPadding}
        onChange={(val) =>
          builder.setElementData(element.id, {
            bottomPadding: val,
          })
        }
      />
    </div>
  );
}
